/**
 * Business Details Tab Component
 * Comprehensive business information management for super admin
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Building, 
  FileText, 
  User, 
  Phone, 
  Clock,
  Globe,
  Save,
  CheckCircle,
  AlertCircle,
  MapPin,
  Mail
} from 'lucide-react';
import { SuperAdminOnboardingData, getStatusBadgeVariant, getStatusDisplayName } from '@/types/super-admin-onboarding';
import { OnboardingStatus } from '@/types/onboarding';
import { toast } from '@/hooks/use-toast';
import ColorPicker from '@/components/admin/ordering-page/ColorPicker';

interface BusinessDetailsTabProps {
  data: SuperAdminOnboardingData;
  onFieldUpdate: (field: keyof SuperAdminOnboardingData, value: any) => Promise<void>;
  onBulkFieldUpdate?: (updates: Record<string, any>) => Promise<{ success: boolean; changedCount: number }>;
  onStatusUpdate: (status: OnboardingStatus) => Promise<void>;
}

const BusinessDetailsTab: React.FC<BusinessDetailsTabProps> = ({
  data,
  onFieldUpdate,
  onBulkFieldUpdate,
  onStatusUpdate
}) => {
  const [formData, setFormData] = useState({
    // Basic Information
    restaurant_name: data.restaurant_name || '',
    organisation_name: data.organisation_name || '',
    address: data.address || '',
    email: data.email || '',
    phone: data.phone || '',
    contact_person: data.contact_person || '',
    
    // Legal Information
    nzbn: data.nzbn || '',
    company_name: data.company_name || '',
    trading_name: data.trading_name || '',
    director_name: data.director_name || '',
    director_mobile_number: data.director_mobile_number || '',
    gst_number: data.gst_number || '',
    
    // Operational Information
    venue_operating_hours: data.venue_operating_hours || '',
    default_preparation_time_minutes: data.default_preparation_time_minutes || 15,
    
    // Social Media
    facebook_url: data.facebook_url || '',
    instagram_url: data.instagram_url || '',
    google_review_url: data.google_review_url || '',
    
    // New fields for delivery and branding
    ubereats_markup: data.ubereats_markup?.toString() || '',
    primary_color: data.primary_color || '#4093ff',
    secondary_color: data.secondary_color || ''
  });
  
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Update form data when the data prop changes
  useEffect(() => {
    setFormData({
      // Basic Information
      restaurant_name: data.restaurant_name || '',
      organisation_name: data.organisation_name || '',
      address: data.address || '',
      email: data.email || '',
      phone: data.phone || '',
      contact_person: data.contact_person || '',
      
      // Legal Information
      nzbn: data.nzbn || '',
      company_name: data.company_name || '',
      trading_name: data.trading_name || '',
      director_name: data.director_name || '',
      director_mobile_number: data.director_mobile_number || '',
      gst_number: data.gst_number || '',
      
      // Operational Information
      venue_operating_hours: data.venue_operating_hours || '',
      default_preparation_time_minutes: data.default_preparation_time_minutes || 15,
      
      // Social Media
      facebook_url: data.facebook_url || '',
      instagram_url: data.instagram_url || '',
      google_review_url: data.google_review_url || '',
      
      // New fields for delivery and branding
      ubereats_markup: data.ubereats_markup?.toString() || '',
      primary_color: data.primary_color || '#4093ff',
      secondary_color: data.secondary_color || ''
    });
    setHasChanges(false);
  }, [data]);

  // Handle form field changes
  const handleFieldChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  // Save all changes
  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Prepare form data with proper type conversion
      const preparedData = { ...formData };
      
      // Convert numeric fields
      if (preparedData.ubereats_markup && typeof preparedData.ubereats_markup === 'string') {
        preparedData.ubereats_markup = parseFloat(preparedData.ubereats_markup);
      }
      if (preparedData.default_preparation_time_minutes && typeof preparedData.default_preparation_time_minutes === 'string') {
        preparedData.default_preparation_time_minutes = parseInt(preparedData.default_preparation_time_minutes);
      }
      
      if (onBulkFieldUpdate) {
        // Use the new bulk update function
        const result = await onBulkFieldUpdate(preparedData);
        
        if (result.changedCount === 0) {
          toast({
            title: "No Changes",
            description: "No fields were modified"
          });
        } else {
          toast({
            title: "Success",
            description: `Updated ${result.changedCount} field(s) successfully`
          });
        }
      } else {
        // Fallback to individual updates if bulk update not available
        const updates = Object.entries(preparedData).map(([field, value]) => {
          return onFieldUpdate(field as keyof SuperAdminOnboardingData, value);
        });
        
        await Promise.all(updates);
        
        toast({
          title: "Success",
          description: "Business details saved successfully"
        });
      }
      
      setHasChanges(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save business details",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  // Update status
  const handleStatusUpdate = async (status: OnboardingStatus) => {
    try {
      // Save changes first if any
      if (hasChanges) {
        await handleSave();
      }
      
      await onFieldUpdate('business_details_status', status);
      
      toast({
        title: "Success",
        description: `Business details status updated to ${getStatusDisplayName(status)}`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    }
  };

  // Check completion
  const requiredFields = [
    'restaurant_name', 'organisation_name', 'address', 'email', 'phone',
    'contact_person', 'nzbn', 'company_name', 'trading_name',
    'director_name', 'director_mobile_number', 'gst_number'
  ];
  
  const filledRequiredFields = requiredFields.filter(field => 
    formData[field as keyof typeof formData]
  ).length;
  
  const completionPercentage = Math.round((filledRequiredFields / requiredFields.length) * 100);
  const isComplete = data.business_details_status === OnboardingStatus.COMPLETE;
  const canMarkComplete = completionPercentage >= 80; // Allow completion at 80%

  return (
    <div className="space-y-6">
      {/* Header with Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Building className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle>Business Details</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Complete business information and legal details
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={getStatusBadgeVariant(data.business_details_status || OnboardingStatus.NOT_STARTED)}>
                {getStatusDisplayName(data.business_details_status || OnboardingStatus.NOT_STARTED)}
              </Badge>
            </div>
          </div>
          
          {/* Progress Indicator */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Completion Progress</span>
              <span className="text-sm text-muted-foreground">
                {filledRequiredFields}/{requiredFields.length} required fields
              </span>
            </div>
            <div className="w-full bg-secondary rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300" 
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building className="h-5 w-5" />
            Basic Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="restaurant_name">
                Restaurant Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="restaurant_name"
                value={formData.restaurant_name}
                onChange={(e) => handleFieldChange('restaurant_name', e.target.value)}
                placeholder="Enter restaurant name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="organisation_name">
                Organisation Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="organisation_name"
                value={formData.organisation_name}
                onChange={(e) => handleFieldChange('organisation_name', e.target.value)}
                placeholder="Enter organisation name"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">
              <MapPin className="h-4 w-4 inline mr-1" />
              Business Address <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="address"
              value={formData.address}
              onChange={(e) => handleFieldChange('address', e.target.value)}
              placeholder="Enter full business address"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">
                <Mail className="h-4 w-4 inline mr-1" />
                Email Address <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                placeholder="contact@restaurant.com"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">
                <Phone className="h-4 w-4 inline mr-1" />
                Phone Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleFieldChange('phone', e.target.value)}
                placeholder="+64 21 123 4567"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact_person">
              <User className="h-4 w-4 inline mr-1" />
              Primary Contact Person <span className="text-red-500">*</span>
            </Label>
            <Input
              id="contact_person"
              value={formData.contact_person}
              onChange={(e) => handleFieldChange('contact_person', e.target.value)}
              placeholder="Primary contact name"
            />
          </div>
        </CardContent>
      </Card>

      {/* Legal Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Legal Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nzbn">
                NZBN <span className="text-red-500">*</span>
              </Label>
              <Input
                id="nzbn"
                value={formData.nzbn}
                onChange={(e) => handleFieldChange('nzbn', e.target.value)}
                placeholder="New Zealand Business Number"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="gst_number">
                GST Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="gst_number"
                value={formData.gst_number}
                onChange={(e) => handleFieldChange('gst_number', e.target.value)}
                placeholder="GST registration number"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">
                Company Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => handleFieldChange('company_name', e.target.value)}
                placeholder="Legal company name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="trading_name">
                Trading Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="trading_name"
                value={formData.trading_name}
                onChange={(e) => handleFieldChange('trading_name', e.target.value)}
                placeholder="Trading as name"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="director_name">
                Director Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="director_name"
                value={formData.director_name}
                onChange={(e) => handleFieldChange('director_name', e.target.value)}
                placeholder="Company director name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="director_mobile_number">
                Director Mobile <span className="text-red-500">*</span>
              </Label>
              <Input
                id="director_mobile_number"
                type="tel"
                value={formData.director_mobile_number}
                onChange={(e) => handleFieldChange('director_mobile_number', e.target.value)}
                placeholder="+64 21 123 4567"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Operational Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Operational Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="venue_operating_hours">Operating Hours</Label>
            <Textarea
              id="venue_operating_hours"
              value={formData.venue_operating_hours}
              onChange={(e) => handleFieldChange('venue_operating_hours', e.target.value)}
              placeholder="e.g., Mon-Fri: 11:00 AM - 10:00 PM, Sat-Sun: 10:00 AM - 11:00 PM"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="default_preparation_time_minutes">
              Default Preparation Time (minutes)
            </Label>
            <Select
              value={formData.default_preparation_time_minutes?.toString() || '15'}
              onValueChange={(value) => handleFieldChange('default_preparation_time_minutes', parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select preparation time" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 56 }, (_, i) => i + 5).map(minutes => (
                  <SelectItem key={minutes} value={minutes.toString()}>
                    {minutes} minutes
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Social Media */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Social Media & Online Presence
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="facebook_url">Facebook Page URL</Label>
            <Input
              id="facebook_url"
              type="url"
              value={formData.facebook_url}
              onChange={(e) => handleFieldChange('facebook_url', e.target.value)}
              placeholder="https://facebook.com/yourrestaurant"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="instagram_url">Instagram Profile URL</Label>
            <Input
              id="instagram_url"
              type="url"
              value={formData.instagram_url}
              onChange={(e) => handleFieldChange('instagram_url', e.target.value)}
              placeholder="https://instagram.com/yourrestaurant"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="google_review_url">Google Business Profile Review Request URL</Label>
            <Input
              id="google_review_url"
              type="url"
              value={formData.google_review_url}
              onChange={(e) => handleFieldChange('google_review_url', e.target.value)}
              placeholder="https://g.page/yourrestaurant"
            />
          </div>
        </CardContent>
      </Card>

      {/* Delivery & Branding Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building className="h-5 w-5" />
            Delivery & Branding Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ubereats_markup">UberEats Markup (%)</Label>
            <Input
              id="ubereats_markup"
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={formData.ubereats_markup}
              onChange={(e) => handleFieldChange('ubereats_markup', e.target.value)}
              placeholder="0.0"
            />
            <p className="text-xs text-muted-foreground">
              Markup percentage applied to menu items on UberEats
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <ColorPicker
                label="Primary Brand Color"
                value={formData.primary_color || '#4093ff'}
                onChange={(value) => handleFieldChange('primary_color', value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Primary color used in branding and UI elements
              </p>
            </div>

            <div>
              <ColorPicker
                label="Secondary Brand Color"
                value={formData.secondary_color || '#000000'}
                onChange={(value) => handleFieldChange('secondary_color', value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Secondary color used for accents and contrasts
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={handleSave} 
              disabled={!hasChanges || saving}
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            
            {canMarkComplete && !isComplete && (
              <Button 
                variant="default"
                onClick={() => handleStatusUpdate(OnboardingStatus.COMPLETE)}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Mark as Complete
              </Button>
            )}
            
            {!isComplete && (
              <Button 
                variant="secondary"
                onClick={() => handleStatusUpdate(OnboardingStatus.AWAITING_CONFIRMATION)}
              >
                Mark as Awaiting Confirmation
              </Button>
            )}
            
            <Button 
              variant="outline"
              onClick={() => handleStatusUpdate(OnboardingStatus.NEEDS_ATTENTION)}
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Mark as Needs Attention
            </Button>
          </div>
          
          <div className="p-4 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground">
              Complete at least {Math.ceil(requiredFields.length * 0.8)} of the {requiredFields.length} required fields to mark this section as complete.
              Currently {filledRequiredFields} fields are filled ({completionPercentage}% complete).
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BusinessDetailsTab;